import { useMemo, useCallback, useRef } from 'react';
import { listBoats } from '../../api/boats/index.js';
import { useBoatFiltersContext } from '../../contexts/boat/filters.js';
import { useBoatListContext } from '../../contexts/boat/list.js';
import { useLazyEffect } from '../effects/lazy.js';
import { useLanguage } from '../locale/language.js';
import { logDebug, logWarn } from '../../utils/debug/logger.js';
import { isAbortWarning } from '../../types/error/abort.js';

function useBoatsCount() {
    const { meta } = useBoatListContext();
    return meta && meta.count;
}
function useCurrentPage() {
    const { filters } = useBoatFiltersContext();
    return filters ? (filters.page || 1) : null;
}
function usePagesCount() {
    const { meta, boats } = useBoatListContext();
    const { filters } = useBoatFiltersContext();
    return useMemo(() => ((meta && boats) ? (meta.nextPage == null
        ? (filters.page || 1)
        : Math.ceil(meta.count / boats.length)) : null), [meta, boats]);
}
function useFilteredBoatList(onChange) {
    const { boats, setBoats, fetching, setFetching, setMeta, } = useBoatListContext();
    const { filters, querystring } = useBoatFiltersContext();
    const [language] = useLanguage();
    const fetchQuery = useRef(querystring);
    useLazyEffect(async (signal) => {
        try {
            logDebug('BoatList: fetching with filters', filters);
            setFetching(true);
            fetchQuery.current = querystring;
            const res = await listBoats(filters, { signal });
            if (!signal.aborted) {
                logDebug('BoatList: results', res.results);
                setMeta({
                    count: res.count,
                    nextPage: res.next ? ((filters.page || 1) + 1) : null,
                    previousPage: (res.previous && filters.page)
                        ? (filters.page - 1) : null,
                });
                onChange(res.results, setBoats, filters);
                setFetching(false);
            }
        }
        catch (error) {
            logWarn('BoatList: fetching error', error);
            if (!isAbortWarning(error))
                setFetching(false);
        }
    }, [filters, language]);
    return [boats, fetching];
}
function useNextPagePrefetch() {
    const { meta } = useBoatListContext();
    const { filters } = useBoatFiltersContext();
    return useCallback(() => {
        if (meta && meta.nextPage) {
            listBoats({ ...filters, page: meta.nextPage });
        }
    }, [filters, meta]);
}

export { useBoatsCount, useCurrentPage, useFilteredBoatList, useNextPagePrefetch, usePagesCount };
//# sourceMappingURL=list.js.map
